import loadable from '@loadable/component';

import { Grid } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { BriefModalOnLoad } from '@/components/BriefModal';
import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart';
import BrazeContentCard from '@/components/BrazeContentCard';

import { useWatchlist, type Watchlist } from '@/hooks/useWatchlist';
import { useSortedWatchlistItems } from '../../hooks/useWatchlistSorting';
import { usePanelWatchlistItemId } from '../../hooks/usePanelWatchlistItemId';
import { useIsMobileScreen } from '@/hooks/useMedia';

import { EventsModal } from '../EventsModal';
import { TableTitle } from '../TableTitle';

import { BrazeContentCardWrapper } from './styled';
import { useWatchlistPeriod } from '../../hooks/useWatchlistPeriod';
import {
  StyledTable,
  Table,
} from '@/components/NarrativeTable/components/Table';
import { PrimaryNarrative } from '@/components/NarrativeTable/components/PrimaryNarrative';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { useOnboardingBanner } from '@/components/NarrativeTable/hooks/useOnboardingBanner';
import { useMemo } from 'react';

const WatchlistPanel = loadable(() => import('../WatchlistPanel'));

const Header = loadable(() => import('../Header'), {
  resolveComponent: (c) => c.Header,
});

const Footer = loadable(() => import('../Footer'), {
  resolveComponent: (c) => c.Footer,
});

const TableActions = loadable(() => import('@/components/NarrativeTable'), {
  resolveComponent: (c) => c.TableActions,
});

const NarrativesBanner = loadable(
  () =>
    import('../../../../components/NarrativeTable/components/NarrativesBanner'),
  {
    resolveComponent: (c) => c.NarrativesBanner,
  }
);

const NarrativesFeedback = loadable(() => import('../NarrativesFeedback'), {
  resolveComponent: (c) => c.NarrativesFeedback,
});

const FloatingAddToWatchlist = loadable(
  () => import('../FloatingAddToWatchlist'),
  {
    resolveComponent: (c) => c.FloatingAddToWatchlist,
  }
);
const RowDesktop = loadable(
  () => import('@/components/NarrativeTable/components/TableRow'),
  {
    resolveComponent: (c) => c.RowDesktop,
  }
);
const RowExpanded = loadable(
  () => import('@/components/NarrativeTable/components/TableRow'),
  {
    resolveComponent: (c) => c.RowExpanded,
  }
);
const RowSkeleton = loadable(
  () => import('@/components/NarrativeTable/components/TableRow'),
  {
    resolveComponent: (c) => c.RowSkeleton,
  }
);
const RowMobile = loadable(
  () => import('@/components/NarrativeTable/components/TableRow'),
  {
    resolveComponent: (c) => c.RowMobile,
  }
);

const WatchListMoreOptions = loadable(
  () => import('@/components/NarrativeTable/components/MoreOptions'),
  {
    resolveComponent: (c) => c.WatchListMoreOptions,
  }
);

export const PopulatedState = ({ watchlist }: { watchlist: Watchlist }) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const sortedWatchlistItems = useSortedWatchlistItems(watchlist?.items);
  const defaultWatchlistItemId = sortedWatchlistItems[0]?.id ?? null;
  const [panelWatchlistItemId, setPanelWatchlistItemId] =
    usePanelWatchlistItemId({
      defaultId: defaultWatchlistItemId,
    });

  const { status } = useWatchlist();
  const [watchlistPeriod] = useWatchlistPeriod();

  const isLoading = status === 'pending';

  const { isBannerDismissed, handleBannerDismissed } = useOnboardingBanner();

  const hasAutoAddedCompany = useMemo(() => {
    if (status !== 'success' || typeof watchlist.items === 'undefined') {
      return false;
    }
    return watchlist.items.some((item) => item.autoAdded);
  }, [watchlist.items, status]);

  const hasValuationSet = watchlist.items.some(
    (item) => item.company.myValuation !== null
  );

  const showNarrativesBanner = !isBannerDismissed && !hasValuationSet;

  const shouldShowNarrativeOnboarding = (index: number) =>
    index === 0 && !isBannerDismissed && !hasValuationSet;

  const NARRATIVE_BANNER_CONTENT = hasAutoAddedCompany
    ? "We've added your recently viewed stocks. Set your fair value to track your investment."
    : 'Select your fair value and track your investment with narratives.';

  const renderTable = () => (
    <Table itemId={panelWatchlistItemId}>
      {sortedWatchlistItems.map((item, index) => {
        if (isMobileScreen) {
          return (
            <RowMobile
              key={item.id}
              selected={panelWatchlistItemId === item.id}
              index={index}
              item={item}
              onItemSelect={setPanelWatchlistItemId}
              period={watchlistPeriod}
              moreOptions={
                <WatchListMoreOptions
                  company={item.company}
                  listId={watchlist.id}
                />
              }
              primaryNarrative={({ selectedDate, selectedFairValue }) => (
                <PrimaryNarrative
                  companyId={item.company.id}
                  companyName={item.company.name}
                  tickerSymbol={item.company.tickerSymbol}
                  currencyISO={
                    getTradingItemCurrencyISO(item.company.data.currencyInfo) ??
                    'XXX'
                  }
                  myValuation={item.company.myValuation}
                  defaultNarrative={item.company.defaultNarrative}
                  isOnboardingNarrative={shouldShowNarrativeOnboarding(index)}
                  selectedDate={selectedDate}
                  selectedFairValue={selectedFairValue}
                />
              )}
            />
          );
        }

        if (panelWatchlistItemId) {
          return (
            <RowDesktop
              key={item.id}
              selected={panelWatchlistItemId === item.id}
              index={index}
              item={item}
              onItemSelect={setPanelWatchlistItemId}
              period={watchlistPeriod}
              moreOptions={
                <WatchListMoreOptions
                  company={item.company}
                  listId={watchlist.id}
                />
              }
              primaryNarrative={({ selectedDate, selectedFairValue }) => (
                <PrimaryNarrative
                  companyId={item.company.id}
                  companyName={item.company.name}
                  tickerSymbol={item.company.tickerSymbol}
                  currencyISO={
                    getTradingItemCurrencyISO(item.company.data.currencyInfo) ??
                    'XXX'
                  }
                  myValuation={item.company.myValuation}
                  defaultNarrative={item.company.defaultNarrative}
                  isOnboardingNarrative={shouldShowNarrativeOnboarding(index)}
                  selectedDate={selectedDate}
                  selectedFairValue={selectedFairValue}
                />
              )}
            />
          );
        }

        return (
          <RowExpanded
            key={item.id}
            index={index}
            listItem={item}
            onItemSelect={setPanelWatchlistItemId}
            period={watchlistPeriod}
            moreOptions={
              <WatchListMoreOptions
                company={item.company}
                listId={watchlist.id}
              />
            }
            primaryNarrative={({ selectedDate, selectedFairValue }) => (
              <PrimaryNarrative
                companyId={item.company.id}
                companyName={item.company.name}
                tickerSymbol={item.company.tickerSymbol}
                currencyISO={
                  getTradingItemCurrencyISO(item.company.data.currencyInfo) ??
                  'XXX'
                }
                myValuation={item.company.myValuation}
                defaultNarrative={item.company.defaultNarrative}
                isOnboardingNarrative={shouldShowNarrativeOnboarding(index)}
                selectedDate={selectedDate}
                selectedFairValue={selectedFairValue}
              />
            )}
          />
        );
      })}
    </Table>
  );

  if (isLoading) {
    return (
      <StyledTable>
        <RowSkeleton />
        <RowSkeleton />
        <RowSkeleton />
        <RowSkeleton />
        <RowSkeleton />
      </StyledTable>
    );
  }

  return (
    <>
      <MiniPriceChartDefinitions />
      <BrazeContentCardWrapper>
        <BrazeContentCard
          feedType="watchlist.top"
          anchorTarget="_blank"
          className="px-x2 pb-x1 lg:px-0"
        />
      </BrazeContentCardWrapper>
      <Header watchlist={watchlist} />
      {isMobileScreen && <FloatingAddToWatchlist watchlist={watchlist} />}
      {isMobileScreen ? (
        <>
          <Grid
            templateColumns="auto auto"
            justifyContent="space-between"
            padding="x2"
          >
            <TableTitle itemCount={watchlist.items.length} title="Watching" />
            <TableActions
              onToggleTableExpansion={() => {
                setPanelWatchlistItemId((id) =>
                  id ? null : defaultWatchlistItemId
                );
              }}
            />
          </Grid>
          {showNarrativesBanner && (
            <NarrativesBanner
              handleBannerDismissed={handleBannerDismissed}
              content={NARRATIVE_BANNER_CONTENT}
            />
          )}
          {renderTable()}
          <Footer watchlist={watchlist} />
          <NarrativesFeedback />
          {panelWatchlistItemId ? (
            <WatchlistPanel
              watchlistItemId={panelWatchlistItemId}
              setWatchlistItemId={setPanelWatchlistItemId}
            />
          ) : null}
          <EventsModal />
          <BriefModalOnLoad />
        </>
      ) : (
        <Grid
          templateColumnsLg={panelWatchlistItemId ? 'auto 32%' : 'auto'}
          gap="16px"
        >
          <div>
            <Grid
              templateColumns="auto auto"
              justifyContent="space-between"
              paddingY="x2"
            >
              <TableTitle itemCount={watchlist.items.length} title="Watching" />
              <TableActions
                onToggleTableExpansion={() => {
                  setPanelWatchlistItemId((id) =>
                    id ? null : defaultWatchlistItemId
                  );
                }}
              />
            </Grid>
            {showNarrativesBanner && (
              <NarrativesBanner
                handleBannerDismissed={handleBannerDismissed}
                content={NARRATIVE_BANNER_CONTENT}
              />
            )}
            {renderTable()}
            <Footer watchlist={watchlist} />
            <NarrativesFeedback />
          </div>
          {panelWatchlistItemId ? (
            <WatchlistPanel
              watchlistItemId={panelWatchlistItemId}
              setWatchlistItemId={setPanelWatchlistItemId}
            />
          ) : null}
          <EventsModal />
          <BriefModalOnLoad />
        </Grid>
      )}
    </>
  );
};
